import type { Color } from '../types';

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

const cache = new Map<string, Rgba | null>();

/** Parse '#rgb', '#rgba', '#rrggbb', '#rrggbbaa', 'rgb(r,g,b)', 'rgba(r,g,b,a)'. Returns null if invalid. */
export function parseColor(css: Color): Rgba | null {
  if (cache.has(css)) return cache.get(css)!;
  const s = css.trim();
  let out: Rgba | null = null;
  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (/^[0-9a-f]{3,4}$/i.test(hex)) {
      const v = hex.split('').map((c) => parseInt(c + c, 16));
      out = { r: v[0], g: v[1], b: v[2], a: hex.length === 4 ? v[3] / 255 : 1 };
    } else if (/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex)) {
      out = {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
      };
    }
  } else {
    const m = /^rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i.exec(s);
    if (m) {
      let a = 1;
      if (m[4] != null) a = m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
      out = { r: +m[1], g: +m[2], b: +m[3], a: Math.min(1, Math.max(0, a)) };
    }
  }
  cache.set(css, out);
  return out;
}

export function isColor(css: string): boolean {
  return parseColor(css) !== null;
}

/** CSS colour -> 0xRRGGBB for PixiJS. Unknown strings become white. */
export function toPixiColor(css: Color): number {
  const c = parseColor(css);
  if (!c) return 0xffffff;
  return ((c.r & 255) << 16) | ((c.g & 255) << 8) | (c.b & 255);
}

/** Alpha channel of a CSS colour (1 when opaque or unparseable). */
export function alphaOf(css: Color): number {
  return parseColor(css)?.a ?? 1;
}

/** Same colour with a different alpha, as rgba(). */
export function withAlpha(css: Color, alpha: number): string {
  const c = parseColor(css) ?? { r: 255, g: 255, b: 255, a: 1 };
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${Math.min(1, Math.max(0, alpha))})`;
}
